import { take, takeEvery, call, fork, cancel, cancelled, put, actionChannel, flush } from 'redux-saga/effects';
import ky from 'ky-universal';
import { schema, normalize } from 'normalizr';
import { stringify } from 'query-string';

import { apiHost } from '../../config/api';
import baseFields from '../../config/api/baseFields';
import { AUTH_STATE_LOGIN } from '../../constants';
import authUtils from '../utils/auth';

import {
  AUTH_FLOW_END,
  FETCH_FAVORITE_DRAMAS_REQUESTING,
  FETCH_FAVORITE_DRAMAS_ABORTED,
  FETCH_FAVORITE_DRAMAS_FAILED,
  FETCH_FAVORITE_DRAMAS_SUCCEEDED,
  ADD_FAVORITE_DRAMA,
  REMOVE_FAVORITE_DRAMA,
  LOGOUT,
} from '../constants/actionTypes';

const endpoint = `${apiHost}/user/favoriteDrama`;

function* fetchFavoriteDramas() {
  const abortController = new AbortController();

  try {
    yield put({ type: FETCH_FAVORITE_DRAMAS_REQUESTING });

    const res = yield call(ky.get, endpoint, {
      signal: abortController.signal,
      searchParams: stringify(baseFields),
      headers: { Authorization: authUtils.getAccessToken() },
    });

    const { data } = yield res.json();

    const favoriteDramas = data.map((item) => {
      return {
        dramaId: item.DramaId,
        dramaName: item.Drama.DramaInfo.name,
        dramaPoster: item.Drama.verticalPosterUrl,
      };
    });

    const favoriteDramasSchema = [new schema.Entity('favoriteDramas', undefined, { idAttribute: 'dramaId' })];

    yield put({ type: FETCH_FAVORITE_DRAMAS_SUCCEEDED, payload: normalize(favoriteDramas, favoriteDramasSchema) });
  } catch (error) {
    yield put({ type: FETCH_FAVORITE_DRAMAS_FAILED, payload: error, error: true });
  } finally {
    if (yield cancelled()) {
      abortController.abort();
      yield put({ type: FETCH_FAVORITE_DRAMAS_ABORTED });
    }
  }
}

export function* fetchFavoriteDramasFlow() {
  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      const task = yield fork(fetchFavoriteDramas);
      yield take(LOGOUT);
      yield cancel(task);
    }
  }
}

/**
 * @param {AbortSignal} signal
 */
function* addFavoriteDrama(signal, action) {
  const req = ky.put(endpoint, {
    signal,
    json: { ...baseFields, dramaId: action.payload },
    headers: { Authorization: authUtils.getAccessToken() },
  });

  yield put({ ...action, optimistic: req });
}

export function* addFavoriteDramaFlow() {
  const queue = yield actionChannel(ADD_FAVORITE_DRAMA);

  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(FETCH_FAVORITE_DRAMAS_SUCCEEDED);
      const abortController = new AbortController();
      const tasks = yield takeEvery(queue, addFavoriteDrama, abortController.signal);
      yield take(LOGOUT);
      yield cancel(tasks);
      abortController.abort();
    }

    yield flush(queue);
  }
}

/**
 * @param {AbortSignal} signal
 */
function* removeFavoriteDrama(signal, action) {
  const req = ky.delete(endpoint, {
    signal,
    searchParams: stringify({ ...baseFields, dramaId: action.payload }),
    headers: { Authorization: authUtils.getAccessToken() },
  });

  yield put({ ...action, optimistic: req });
}

export function* removeFavoriteDramaFlow() {
  const queue = yield actionChannel(REMOVE_FAVORITE_DRAMA);

  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(FETCH_FAVORITE_DRAMAS_SUCCEEDED);
      const abortController = new AbortController();
      const tasks = yield takeEvery(queue, removeFavoriteDrama, abortController.signal);
      yield take(LOGOUT);
      yield cancel(tasks);
      abortController.abort();
    }

    yield flush(queue);
  }
}
